import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, BarChart3, Info } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BarList = ({ title, subtitle, rows, labels, total, accent, testId }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div
      className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm"
      data-testid={testId}
    >
      <h3 className="font-heading font-bold text-base text-galvani-text mb-1">{title}</h3>
      {subtitle && (
        <p className="font-body text-xs text-galvani-textLight mb-5">{subtitle}</p>
      )}
      {rows.length === 0 ? (
        <p className="font-body text-sm text-slate-400 py-4">Todavía sin datos.</p>
      ) : (
        <div className="space-y-3.5">
          {rows.map((r) => (
            <div key={r.value}>
              <div className="flex items-baseline justify-between gap-3 mb-1.5">
                <span className="font-body text-sm text-galvani-text">
                  {labels[r.value] || r.value}
                </span>
                <span className="font-body text-xs font-semibold text-galvani-textLight whitespace-nowrap">
                  {r.count}
                  {total > 0 && (
                    <span className="text-slate-400">
                      {" "}
                      · {Math.round((r.count / total) * 100)}%
                    </span>
                  )}
                </span>
              </div>
              <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full rounded-full bg-gradient-to-r ${accent} transition-[width] duration-700`}
                  style={{ width: `${(r.count / max) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function LeadsInsights({
  authHeaders,
  beneficioLabels,
  objetivoLabels,
  modalidadLabels,
  presupuestoLabels,
  timelineLabels,
  clientesLabels,
  rolLabels,
  origenLabels,
}) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [origen, setOrigen] = useState("all");

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = origen === "all" ? "" : `?origen=${origen}`;
      const res = await axios.get(`${API}/leads/insights${params}`, { headers: authHeaders() });
      setData(res.data);
    } finally {
      setIsLoading(false);
    }
  }, [origen, authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  if (isLoading) {
    return (
      <div className="p-16 flex items-center justify-center rounded-2xl bg-white border border-slate-200">
        <Loader2 className="w-8 h-8 text-galvani-purple animate-spin" />
      </div>
    );
  }

  const total = data?.total || 0;

  return (
    <div className="space-y-5" data-testid="leads-insights">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between p-5 rounded-2xl bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-100">
        <div className="flex items-start gap-3">
          <BarChart3 className="w-5 h-5 text-galvani-purple flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-heading font-bold text-base text-galvani-text">
              {total} {total === 1 ? "lead analizado" : "leads analizados"}
            </p>
            <p className="font-body text-xs text-galvani-textLight">
              Qué motivos eligen más tus leads. Úsalo para afinar los titulares y los anuncios.
            </p>
          </div>
        </div>
        <Select value={origen} onValueChange={setOrigen}>
          <SelectTrigger className="h-11 rounded-xl bg-white sm:w-56" data-testid="insights-origen">
            <SelectValue placeholder="Origen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las landings</SelectItem>
            {Object.entries(origenLabels).map(([v, l]) => (
              <SelectItem key={v} value={v}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <BarList
          title="¿En qué creen que les puede ayudar el videopodcast?"
          subtitle="Respuestas múltiples: un lead puede marcar varias."
          rows={data?.beneficios || []}
          labels={beneficioLabels}
          total={total}
          accent="from-galvani-blue to-galvani-purple"
          testId="insights-beneficios"
        />
        <BarList
          title="Objetivo principal"
          rows={data?.objetivos || []}
          labels={objetivoLabels}
          total={total}
          accent="from-galvani-purple to-galvani-pink"
          testId="insights-objetivos"
        />
        <BarList
          title="Dónde quieren grabar"
          rows={data?.modalidades || []}
          labels={modalidadLabels}
          total={total}
          accent="from-galvani-cyan to-galvani-blue"
          testId="insights-modalidades"
        />
        <BarList
          title="Presupuesto declarado"
          rows={data?.presupuestos || []}
          labels={presupuestoLabels}
          total={total}
          accent="from-emerald-500 to-emerald-700"
          testId="insights-presupuestos"
        />
        <BarList
          title="Cuándo quieren empezar"
          rows={data?.timelines || []}
          labels={timelineLabels}
          total={total}
          accent="from-amber-500 to-orange-600"
          testId="insights-timelines"
        />
        <BarList
          title="Perfil / rol"
          rows={data?.roles || []}
          labels={rolLabels}
          total={total}
          accent="from-slate-500 to-slate-700"
          testId="insights-roles"
        />
        {(data?.clientes_potenciales || []).length > 0 && (
          <BarList
            title="Clientes que encajarían (agencias)"
            rows={data.clientes_potenciales}
            labels={clientesLabels}
            total={total}
            accent="from-galvani-pink to-rose-600"
            testId="insights-clientes"
          />
        )}
      </div>

      <div className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
        <Info className="w-4 h-4 text-galvani-textLight flex-shrink-0 mt-0.5" />
        <p className="font-body text-xs text-galvani-textLight">
          Los porcentajes se calculan sobre el total de leads del origen seleccionado. En las
          respuestas múltiples la suma puede pasar del 100%.
        </p>
      </div>
    </div>
  );
}
